import { useEffect, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  PHONE_COUNTRY_CODES,
  DEFAULT_COUNTRY_CODE,
  buildFullPhone,
  splitPhoneDigits,
  findCountryByCode,
  countryOptionLabel,
  normPhoneDigits,
} from '../../utils/phoneCountryCodes';

const darkInputSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    borderRadius: 2,
    '& fieldset': { borderColor: 'rgba(255,255,255,.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,.35)' },
  },
};

const lightInputSx = {};

/**
 * Teléfono con código de país (Autocomplete filtrable + número libre).
 * `value` / `onChange` usan solo dígitos E.164 sin "+" (ej. 59177858921).
 */
export default function PhoneWithCountryField({
  value = '',
  onChange,
  label = 'WhatsApp',
  required = false,
  helperText,
  error = false,
  errorText,
  disabled = false,
  size = 'small',
  theme = 'light',
  fullWidth = true,
}) {
  const parsed = useMemo(() => splitPhoneDigits(value), [value]);
  const [country, setCountry] = useState(() => findCountryByCode(parsed.countryCode));
  const [national, setNational] = useState(parsed.national);

  useEffect(() => {
    const next = splitPhoneDigits(value);
    setCountry(findCountryByCode(next.countryCode));
    setNational(next.national);
  }, [value]);

  const emit = (cc, nn) => {
    onChange?.(buildFullPhone(cc, nn));
  };

  const inputSx = theme === 'dark' ? darkInputSx : lightInputSx;
  const labelSx = theme === 'dark' ? { sx: { color: 'rgba(255,255,255,.6)' } } : {};

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
        <Autocomplete
          options={PHONE_COUNTRY_CODES}
          value={country}
          onChange={(_, next) => {
            const picked = next || findCountryByCode(DEFAULT_COUNTRY_CODE);
            setCountry(picked);
            emit(picked.code, national);
          }}
          getOptionLabel={countryOptionLabel}
          isOptionEqualToValue={(a, b) => a?.code === b?.code}
          disabled={disabled}
          size={size}
          sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }}
          filterOptions={(options, { inputValue }) => {
            const q = String(inputValue || '').trim().toLowerCase().replace(/\D/g, '');
            if (!q) return options;
            return options.filter(
              (o) =>
                o.label.toLowerCase().includes(q) ||
                o.code.includes(q) ||
                o.iso.toLowerCase().includes(q)
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="País"
              placeholder="Buscar país o +591"
              required={required}
              InputLabelProps={labelSx}
              sx={inputSx}
            />
          )}
        />
        <TextField
          label={label}
          placeholder="Número sin código"
          value={national}
          onChange={(e) => {
            const nn = normPhoneDigits(e.target.value);
            setNational(nn);
            emit(country?.code || DEFAULT_COUNTRY_CODE, nn);
          }}
          required={required}
          disabled={disabled}
          size={size}
          fullWidth
          autoComplete="tel"
          InputLabelProps={labelSx}
          sx={{ ...inputSx, flex: 1, minWidth: 0 }}
        />
      </Stack>
      {(helperText || errorText) && (
        <Typography
          variant="caption"
          sx={{
            mt: 0.75,
            display: 'block',
            color: error ? '#f87171' : theme === 'dark' ? 'rgba(255,255,255,.5)' : 'text.secondary',
          }}
        >
          {error ? errorText : helperText}
        </Typography>
      )}
    </Box>
  );
}
